import { Button, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Desc, Title } from "../../../components/Desc";
import ProgressIndicator from "../../../components/ProgressIndicator";
import strings from "../../../config/localization";
import {
  useAutoPersonnelAllocationsMutation,
  useGetPersonnelAssessmentsOfTimeslotQuery,
} from "../../../services/appln";
import { getLocationFilters } from "../../util";

interface DataType {
  key: string;
  locationId: string;
  designationId: string;
  name: string;
  kind: string;
  parent: string;
  designationName: string;
  assessmentId: number;
  assessmentCount: number;
  allocatedCount: number;
  status: string;
}

interface Data {
  rowData: DataType[];
  columnData: ColumnsType<DataType>;
}

export default function PersonnelAllocationSummary() {
  console.log("PersonnelAllocationSummary");
  const params = useParams();
  console.log("PersonnelAllocationSummary, params:", params);
  const navigate = useNavigate();

  const bandobastContext = JSON.parse(localStorage.getItem("bandobast")!);
  const timeslotContext = JSON.parse(localStorage.getItem("timeslot")!);

  const [autoAllocate, autoAllocateParams] =
    useAutoPersonnelAllocationsMutation();

  const [tableData, setTableData] = React.useState<Data>({
    rowData: [],
    columnData: [],
  });

  const assessmentsQueryResponse = useGetPersonnelAssessmentsOfTimeslotQuery({
    bandobastId: params.bandobastId,
    timeslotId: params.timeslotId,
  });

  React.useEffect(() => {
    console.log(
      "PersonnelAllocationSummary, response:",
      assessmentsQueryResponse,
    );
    if (!assessmentsQueryResponse.isSuccess) {
      return;
    }

    const rowData = assessmentsQueryResponse.data.data.map((e: any) => {
      const key = `${e.locationId}:${e.designationId}`;
      return {
        key: key,
        locationId: e.locationId,
        designationId: e.designationId,
        name: e.locationName,
        kind: e.locationKind,
        parent: "",
        designationName: e.designationName,
        assessmentCount: e.count,
        allocationCount: e.allocationCount,
        assessmentId: e.id,
        status:
          e.count === e.allocationCount ? strings.allocated : strings.pending,
      };
    });

    const designationNameSet = new Set<string>(
      rowData.map((e: DataType) => e.designationName),
    );

    const columns: ColumnsType<DataType> = [
      {
        title: strings.name,
        dataIndex: "name",
        key: "name",
      },
      {
        title: strings.kind,
        dataIndex: "kind",
        key: "kind",
        filters: getLocationFilters(),
        onFilter: (value: any, record) => {
          return record.kind === (value as string);
        },
      },
      {
        title: strings.parent,
        dataIndex: "parent",
        key: "parent",
      },
      {
        title: strings.designation,
        dataIndex: "designationName",
        key: "designationName",
        filters: Array.from(designationNameSet.keys())
          .sort()
          .map((e) => {
            return { text: e, value: e };
          }),
        onFilter: (value: any, record) => {
          return record.designationName === (value as string);
        },
      },
      {
        title: strings.assessmentCount,
        dataIndex: "assessmentCount",
        key: "assessmentCount",
        render: (text, record, index) => (
          <Button
            onClick={() => {
              navigate(
                `../personnelAssessments/${params.bandobastId}/${timeslotContext.id}/${record.locationId}`,
              );
            }}
          >
            {text as string}
          </Button>
        ),
      },
      {
        title: strings.allocatedCount,
        dataIndex: "allocationCount",
        key: "allocationCount",
        render: (text, record, index) => (
          <Button
            onClick={() => {
              const toStore = {
                id: record.locationId,
                name: record.name,
                designationId: record.designationId,
                designationName: record.designationName,
                count: record.assessmentCount,
                assessmentId: record.assessmentId,
              };
              localStorage.setItem("location", JSON.stringify(toStore));
              navigate(
                `../personnelAllocation/${params.bandobastId}/${params.timeslotId}/${record.locationId}/${record.designationId}`,
              );
            }}
          >
            {text as string}
          </Button>
        ),
      },
      {
        title: strings.status,
        dataIndex: "status",
        key: "status",
        filters: [strings.allocated, strings.pending].sort().map((e) => {
          return { text: e, value: e };
        }),
        onFilter: (value: any, record) => {
          return record.status === (value as string);
        },
      },
    ];
    setTableData({
      rowData: rowData,
      columnData: columns,
    });
  }, [assessmentsQueryResponse]);

  console.log("PersonnelAllocationSummary, tableData", tableData);

  return (
    <Box sx={{ flexGrow: 1, width: "100%" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Title text={strings.personnelAllocationSummary} />
        </Grid>
        {(assessmentsQueryResponse.isLoading ||
          assessmentsQueryResponse.isFetching ||
          autoAllocateParams.isLoading) && <ProgressIndicator />}

        <>
          <Grid item xs={12}>
            <Stack spacing={0}>
              <Desc label={strings.bandobast} value={bandobastContext.name} />
              <Desc label={strings.timeslot} value={timeslotContext.name} />
              <Desc
                label={strings.startTime}
                value={timeslotContext.startTime}
              />
              <Desc label={strings.endTime} value={timeslotContext.endTime} />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              id={"autoAllocate"}
              onClick={() => {
                autoAllocate({
                  bandobastId: params.bandobastId,
                  timeslotId: params.timeslotId,
                  allocatePersonnel: true,
                  allocateResources: false,
                });
              }}
            >
              {strings.allocate}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Table
              virtual
              scroll={{ y: 600 }}
              columns={tableData.columnData}
              dataSource={tableData.rowData}
              pagination={false}
            />
          </Grid>
        </>
      </Grid>
    </Box>
  );
}
